import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { Button, cn } from "@nexus/ui";
import type { NcDictRow } from "@/features/identity-hub/lib/customer-setup";

const CONTROL_CLASS =
  "h-9 w-full rounded-md border bg-background px-3 text-[13px] text-foreground outline-none placeholder:text-muted-foreground focus:border-ring focus:ring-1 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50";

interface KeyValueEditorProps {
  id: string;
  rows: readonly NcDictRow[];
  onChange: (rows: NcDictRow[]) => void;
  keyLabel: string;
  valueLabel: string;
  keyPlaceholder: string;
  valuePlaceholder: string;
  addLabel: string;
  hasError?: boolean;
  /** Id of the error message the inputs point at while `hasError` is set. */
  errorId?: string;
  disabled?: boolean;
}

/** Editor for a map of short text keys to text values; rows are edited one pair at a time, never as raw JSON. */
export function KeyValueEditor({
  id,
  rows,
  onChange,
  keyLabel,
  valueLabel,
  keyPlaceholder,
  valuePlaceholder,
  addLabel,
  hasError,
  errorId,
  disabled,
}: KeyValueEditorProps) {
  const { t } = useTranslation();
  const updateRow = (index: number, patch: Partial<NcDictRow>) =>
    onChange(rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));

  return (
    <div className="flex flex-col gap-2" id={id} data-testid="key-value-editor">
      {rows.map((row, index) => (
        <div key={index} className="flex items-center gap-2" data-testid="key-value-row">
          <input
            type="text"
            aria-label={`${keyLabel} ${index + 1}`}
            aria-invalid={hasError || undefined}
            aria-describedby={hasError ? errorId : undefined}
            placeholder={keyPlaceholder}
            value={row.key}
            disabled={disabled}
            onChange={(event) => updateRow(index, { key: event.target.value })}
            className={cn(CONTROL_CLASS, "max-w-[160px] font-mono text-xs", hasError ? "border-destructive" : "border-input-border")}
          />
          <span aria-hidden className="text-muted-foreground">
            →
          </span>
          <input
            type="text"
            aria-label={`${valueLabel} ${index + 1}`}
            aria-invalid={hasError || undefined}
            aria-describedby={hasError ? errorId : undefined}
            placeholder={valuePlaceholder}
            value={row.value}
            disabled={disabled}
            onChange={(event) => updateRow(index, { value: event.target.value })}
            className={cn(CONTROL_CLASS, "max-w-[280px]", hasError ? "border-destructive" : "border-input-border")}
          />
          <button
            type="button"
            aria-label={t("iam.onboarding.setup.namingConvention.removeMapping", "Remove mapping")}
            disabled={disabled}
            onClick={() => onChange(rows.filter((_, rowIndex) => rowIndex !== index))}
            className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            <Trash2 className="size-3.5" aria-hidden />
          </button>
        </div>
      ))}
      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => onChange([...rows, { key: "", value: "" }])}
          data-testid="key-value-add"
        >
          <Plus className="size-3.5" aria-hidden />
          {addLabel}
        </Button>
      </div>
    </div>
  );
}
